import React, { createContext, useCallback, useContext, useEffect, useMemo, ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  accountRehydrated,
  clearAccountContext,
  setAccountContext,
} from '@/app/store/slices/accountAuthSlice';
import { ACCOUNT_KEYS, clearAccountStorage } from '@/config/accountStorage';
import { accountTokens, onAccountSessionExpired } from '@/services/api/accountAxios';
import { accountAuthApi } from '@/services/endpoints/account/auth';
import {
  isMfaChallenge,
  type AccountContext,
  type AccountMembership,
  type AccountMfaChallenge,
  type AccountSession,
} from '@/services/api/accountTypes';

/**
 * Organization account session (owner / admin at /account).
 *
 * Runs alongside `useAuth`, which owns the candidate/JMS session for the jobs
 * portal. Both can be signed in at once without evicting each other.
 */

interface AccountAuthContextValue {
  context: AccountContext | null;
  isAuthenticated: boolean;
  /** Billing endpoints are owner-only server-side; the UI mirrors that. */
  isOwner: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<AccountMfaChallenge | null>;
  completeMfaSignIn: (pendingToken: string, code: string) => Promise<void>;
  switchOrganization: (membership: AccountMembership) => Promise<void>;
  signOut: () => Promise<void>;
  displayName: string;
  initials: string;
}

const AccountAuthContext = createContext<AccountAuthContextValue | undefined>(undefined);

function toContext(session: AccountSession): AccountContext {
  return {
    user: session.user,
    employee: session.employee,
    organization: session.organization,
    role: session.role,
    organizations: session.organizations,
  };
}

function persist(session: AccountSession): void {
  accountTokens.set(session.tokens.accessToken, session.tokens.refreshToken);
  try {
    localStorage.setItem(ACCOUNT_KEYS.context, JSON.stringify(toContext(session)));
  } catch {
    // noop — storage may be unavailable
  }
}

export function AccountAuthProvider({ children }: { children: ReactNode }) {
  const dispatch = useAppDispatch();
  const context = useAppSelector((state) => state.accountAuth.context);
  const loading = useAppSelector((state) => state.accountAuth.loading);

  // Rehydrate: a stored context without a token is a half-session, so both must
  // be present or neither counts.
  useEffect(() => {
    try {
      const stored = localStorage.getItem(ACCOUNT_KEYS.context);
      if (stored && accountTokens.access) {
        dispatch(setAccountContext(JSON.parse(stored) as AccountContext));
        return;
      }
      if (stored) clearAccountStorage();
    } catch {
      clearAccountStorage();
    }
    dispatch(accountRehydrated());
  }, [dispatch]);

  // The axios layer ends the session when a refresh is rejected outright.
  useEffect(
    () => onAccountSessionExpired(() => dispatch(clearAccountContext())),
    [dispatch]
  );

  const signIn = useCallback(
    async (email: string, password: string) => {
      const result = await accountAuthApi.login(email, password);
      if (isMfaChallenge(result)) return result;
      persist(result);
      dispatch(setAccountContext(toContext(result)));
      return null;
    },
    [dispatch]
  );

  const completeMfaSignIn = useCallback(
    async (pendingToken: string, code: string) => {
      const session = await accountAuthApi.verifyLoginMfa(pendingToken, code);
      persist(session);
      dispatch(setAccountContext(toContext(session)));
    },
    [dispatch]
  );

  /**
   * Re-issue the session against another organization the user belongs to.
   * The switch endpoint returns only tokens + the new role, so the rest of the
   * context is rebuilt from the membership the caller picked.
   */
  const switchOrganization = useCallback(
    async (membership: AccountMembership) => {
      const result = await accountAuthApi.switchOrganization(membership.id);
      accountTokens.set(result.tokens.accessToken, result.tokens.refreshToken);

      const stored = localStorage.getItem(ACCOUNT_KEYS.context);
      const previous = stored ? (JSON.parse(stored) as AccountContext) : null;
      if (!previous) return;

      const next: AccountContext = {
        ...previous,
        organization: {
          id: membership.id,
          name: membership.name,
          slug: membership.slug,
          isActive: membership.isActive,
        },
        role: {
          id: result.organizationRole?.id ?? '',
          name: result.organizationRole?.name ?? membership.role,
        },
      };
      localStorage.setItem(ACCOUNT_KEYS.context, JSON.stringify(next));
      dispatch(setAccountContext(next));
    },
    [dispatch]
  );

  const signOut = useCallback(async () => {
    try {
      await accountAuthApi.logout();
    } catch {
      // Revoking server-side is best-effort; the local session ends regardless.
    }
    clearAccountStorage();
    dispatch(clearAccountContext());
  }, [dispatch]);

  const value = useMemo<AccountAuthContextValue>(() => {
    const first = context?.employee?.firstName ?? '';
    const last = context?.employee?.lastName ?? '';
    const fullName = `${first} ${last}`.trim();
    return {
      context,
      isAuthenticated: Boolean(context),
      isOwner: (context?.role?.name ?? '').trim().toLowerCase() === 'owner',
      loading,
      signIn,
      completeMfaSignIn,
      switchOrganization,
      signOut,
      displayName: fullName || context?.user?.email || 'Your account',
      initials:
        `${first[0] ?? ''}${last[0] ?? ''}`.toUpperCase() ||
        (context?.user?.email?.[0] ?? 'A').toUpperCase(),
    };
  }, [context, loading, signIn, completeMfaSignIn, switchOrganization, signOut]);

  return <AccountAuthContext.Provider value={value}>{children}</AccountAuthContext.Provider>;
}

export function useAccountAuth(): AccountAuthContextValue {
  const value = useContext(AccountAuthContext);
  if (!value) {
    throw new Error('useAccountAuth must be used within an AccountAuthProvider');
  }
  return value;
}
